import { useEffect, useCallback } from 'react';
import { useHeadingStructureContext } from '@context/heading-structure-context';
import { keyForNode } from '@utils/page-headings';
import HeadingStructureHeadingTreeList from './heading-tree-list';
import HeadingStructureHeadingTreeListItem from './heading-tree-list-item';
import HeadingStructureHeadingTreeLoader from './heading-tree-loader';

const HeadingStructureHeadingTree = () => {
	const {
		isLoading,
		expandedKey,
		pageHeadings,
		updateHeadingsTree,
		collapseHeading,
		toggleHeading,
	} = useHeadingStructureContext();

	useEffect(() => {
		updateHeadingsTree();

		return () => {
			collapseHeading();
		};
	}, []);

	/**
	 * @param {import('../../types/heading').Ea11yHeading[]} headings
	 * @param {number | false}                               nestedId
	 * @return {JSX.Element} React element to render.
	 */
	const renderPageHeadings = useCallback(
		(headings, nestedId) => {
			const children = headings.flatMap((heading, i) => {
				const key = keyForNode(heading.node);

				const item = (
					<HeadingStructureHeadingTreeListItem
						key={key}
						id={key}
						level={heading.level}
						content={heading.content}
						status={heading.status}
						violation={heading.violationCode}
						isExpanded={expandedKey === key}
						toggleHeading={() => toggleHeading(heading.node)}
					/>
				);

				if (heading.children.length) {
					const subHeaders = renderPageHeadings(heading.children, i);

					return [item, subHeaders];
				}

				return [item];
			});

			const list = <HeadingStructureHeadingTreeList children={children} />;

			if (false !== nestedId) {
				return <li key={`heading-structure-${nestedId}-nested`}>{list}</li>;
			}

			return list;
		},
		[expandedKey, toggleHeading, keyForNode],
	);

	if (!pageHeadings.length && isLoading) {
		return <HeadingStructureHeadingTreeLoader />;
	}

	return renderPageHeadings(pageHeadings, false);
};

export default HeadingStructureHeadingTree;
